import styled from '@emotion/styled';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'src/shared/components/Button';

import { DateWithSessionSlots, SessionSlot } from '../models/videoSessions';
import { ScheduleCalendarDatesHeader } from './ScheduleCalendarDatesHeader';
import { ScheduleCalendarSlotsList } from './ScheduleCalendarSlotsList';
import { analyticsClient } from 'src/shared/services/analytics';

const NavigationRow = styled.div({
    display: 'flex',
    marginTop: 29,
});

const ButtonsWrapper = styled.div({
    marginBottom: 35,
    textAlign: 'center',
});

interface Props {
    loading: boolean;
    amountOfWeeks: number;
    datesWithSessionSlots: DateWithSessionSlots[];
    onboardingMode?: boolean;
    handleNotNowClick: () => void;
    onBookSessionClick: (selectedSlot: SessionSlot) => void;
    readonly?: boolean;
    disableBack?: boolean;
    onBack: VoidFunction;
}

export const ScheduleCalendar: React.FC<Props> = ({
    loading,
    datesWithSessionSlots,
    amountOfWeeks,
    handleNotNowClick,
    onBookSessionClick,
    readonly,
    disableBack,
    onBack,
}) => {
    const { t } = useTranslation();
    const [selectedSlot, setSelectedSlot] = useState<SessionSlot | null>(null);
    const [sliderWidth, setSliderWidth] = useState(0);
    const [sliderOffset, setSliderOffset] = useState(0);
    const [currentWeekSlide, setCurrentWeekSlide] = useState(0);

    useEffect(() => {
        const marginModifier = 12;

        const offset = currentWeekSlide * sliderWidth * -1 + currentWeekSlide * -marginModifier;

        setSliderOffset(offset);
    }, [sliderWidth, currentWeekSlide]);

    // auto advance to the first week that has at least one session slot available
    useEffect(() => {
        const index = datesWithSessionSlots.findIndex(data => data.slots.length > 0);

        if (index) {
            setCurrentWeekSlide(Math.floor(index / 7));
        }
    }, []);

    const handleGoToPreviousWeek = () => {
        if (currentWeekSlide > 0 && !loading) {
            setCurrentWeekSlide(currentWeekSlide - 1);
        }
    };

    const handleGoToNextWeek = () => {
        if (currentWeekSlide + 1 < amountOfWeeks && !loading) {
            setCurrentWeekSlide(currentWeekSlide + 1);
        }
    };

    const buttonDisabled = readonly || loading || !selectedSlot;

    return (
        <>
            <ScheduleCalendarDatesHeader
                dates={datesWithSessionSlots}
                sliderOffset={sliderOffset}
                sliderWidth={sliderWidth}
                onGoToNextWeekClick={handleGoToNextWeek}
                onGoToPreviousWeekClick={handleGoToPreviousWeek}
                onSliderWidthChange={setSliderWidth}
            />

            <ScheduleCalendarSlotsList
                datesWithSessionSlots={datesWithSessionSlots}
                sliderOffset={sliderOffset}
                loading={loading}
                selectedSlot={selectedSlot}
                onSlotClick={setSelectedSlot}
            />
            <ButtonsWrapper>
                <NavigationRow>
                    <Button
                        onClick={disableBack ? handleNotNowClick : onBack}
                        type="white"
                        width="normal"
                    >
                        {t(disableBack ? 'common.not_now' : 'common.back')}
                    </Button>
                    <Button
                        onClick={() => {
                            analyticsClient.trackClick(
                                'Schedule Session Calendar: Book a session',
                                {
                                    page_name: 'Calendar',
                                },
                            );
                            return selectedSlot && onBookSessionClick(selectedSlot);
                        }}
                        disabled={buttonDisabled}
                        noHorizontalPadding
                        marginLeft={18}
                        width="normal"
                    >
                        {t('videoSessions:schedule_calendar.book_session')}
                    </Button>
                </NavigationRow>
                {!disableBack && (
                    <Button
                        onClick={handleNotNowClick}
                        marginTop={5}
                        type="noBackground"
                        width="normal"
                    >
                        {t('common.not_now')}
                    </Button>
                )}
            </ButtonsWrapper>
        </>
    );
};
